import { useState, type FormEvent } from "react";

import type {
  CreateStageRequest,
  Event,
  Operator,
  OverlapConflict,
  Stage
} from "@attendik/types/admin-schemas";

import { api } from "../../api/client.js";
import { ApiError, isApiError } from "../../api/errors.js";
import { Button } from "../../components/ui/button.js";
import { Card } from "../../components/ui/card.js";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader
} from "../../components/ui/dialog.js";
import { Input } from "../../components/ui/input.js";
import { Label } from "../../components/ui/label.js";
import { Select } from "../../components/ui/select.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../components/ui/table.js";
import { EmptyState, ErrorState, LoadingState } from "../../components/states.js";
import { formatRange, fromDateTimeLocal, toDateTimeLocal } from "../../lib/format.js";
import { useAsync } from "../../lib/useAsync.js";

/**
 * Stages tab (admin CR-01 §3.2 + CR-06 §4.1). Table of stages with
 * order/name/capacity/window/operator-count, an "Add stage" form, an edit
 * panel, and a drill-in subscreen for stage-operator assignment that mirrors
 * the event-level OperatorsTab layout (CR-01 §3.4).
 *
 * CR-06 additions:
 *  - "M" badge on rows with `source === 'meetpoint'`.
 *  - "Sync from Meetpoint" button next to "Add stage", shown only when the
 *    event has MP integration configured (re-uses POST /meetpoint/sync).
 *  - "Convert to manual" action inside the edit panel for meetpoint rows.
 */
export function StagesTab({ event }: { event: Event }): JSX.Element {
  const [editingStage, setEditingStage] = useState<Stage | null>(null);
  const [drillStage, setDrillStage] = useState<Stage | null>(null);
  const [adding, setAdding] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const { data, loading, error, reload } = useAsync(
    () => api.listStages(event.id),
    [event.id]
  );

  // CR-06 §3.5 / §4.1: only show the Sync button when MP integration exists
  // for this event. A 404 here is the expected "not configured" state.
  const mpConfigured = useAsync(async (): Promise<boolean> => {
    try {
      await api.getMeetpointConfig(event.id);
      return true;
    } catch (err) {
      if (isApiError(err) && err.status === 404) {
        return false;
      }
      throw err;
    }
  }, [event.id]);

  // For each stage, count its operators so the table can render the cell
  // without forcing an N+1 round-trip on render. The fetches happen in
  // parallel and the result is keyed by stage_id.
  const counts = useAsync(async (): Promise<Record<string, number>> => {
    const stages = data ?? [];
    if (stages.length === 0) {
      return {};
    }
    const entries = await Promise.all(
      stages.map(async (s): Promise<[string, number]> => {
        const ops = await api.listStageOperators(s.id);
        return [s.id, ops.length];
      })
    );
    return Object.fromEntries(entries);
  }, [data]);

  if (loading) {
    return <LoadingState />;
  }
  if (error || !data) {
    return <ErrorState message={error ?? "Failed to load."} onRetry={reload} />;
  }

  const handleSync = async (): Promise<void> => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      await api.triggerSync(event.id);
      // The sync runs server-side; the UI just reloads the stage list. A
      // production-quality UX would poll the sync log for completion, but
      // for now a brief "started" message and a reload are enough — stage
      // upsert is fast (one MP call + one transaction).
      setSyncMessage(
        "Sync started — pull again in a few seconds to see imported stages."
      );
      // Give the backend a moment, then refresh.
      setTimeout(() => {
        reload();
      }, 1500);
    } catch (err) {
      if (isApiError(err) && err.code === "SYNC_IN_PROGRESS") {
        setSyncMessage("A sync is already running — try again shortly.");
      } else {
        setSyncMessage(
          isApiError(err) ? err.message : "Could not start the sync."
        );
      }
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Stages split an event into named subdivisions (e.g. Main hall, Side
          track). Operators are assigned to a stage to scan badges there.
        </p>
        <div className="flex items-center gap-2">
          {mpConfigured.data ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void handleSync();
              }}
              disabled={syncing}
              data-testid="stages-sync-meetpoint"
            >
              {syncing ? "Syncing…" : "Sync from Meetpoint"}
            </Button>
          ) : null}
          <Button
            size="sm"
            onClick={() => {
              setAdding(true);
              setEditingStage(null);
              setFormError(null);
            }}
          >
            Add stage
          </Button>
        </div>
      </div>
      {syncMessage ? (
        <p className="text-sm text-muted-foreground" role="status">
          {syncMessage}
        </p>
      ) : null}

      <Card>
        {data.length === 0 ? (
          <EmptyState
            title="No stages yet"
            description={
              adding
                ? "Fill in the form below to add the first stage."
                : "Add the first stage to start splitting this event into doors."
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Order</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Capacity</TableHead>
                <TableHead>Window</TableHead>
                <TableHead>Operators</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((stage) => {
                const opCount = counts.data?.[stage.id] ?? 0;
                return (
                  <TableRow
                    key={stage.id}
                    className="cursor-pointer hover:bg-secondary/30"
                    onClick={() => {
                      setEditingStage(stage);
                      setAdding(false);
                      setFormError(null);
                    }}
                    data-testid={`stage-row-${stage.id}`}
                  >
                    <TableCell className="text-muted-foreground">
                      {stage.display_order}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="text-left font-medium text-accent hover:underline"
                          onClick={() => {
                            setEditingStage(stage);
                            setAdding(false);
                            setFormError(null);
                          }}
                        >
                          {stage.name}
                        </button>
                        {stage.source === "meetpoint" ? (
                          <span
                            className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-md border border-accent/40 bg-accent/5 px-1.5 text-[0.625rem] font-semibold uppercase text-accent"
                            title="Imported from Meetpoint — name kept in sync"
                            data-testid={`stage-meetpoint-badge-${stage.id}`}
                          >
                            M
                          </span>
                        ) : null}
                        {stage.enforce_program_eligibility ? (
                          <span
                            className="inline-flex h-5 items-center justify-center rounded-md border border-amber-500/40 bg-amber-500/10 px-1.5 text-[0.625rem] font-semibold uppercase text-amber-700"
                            title="Only attendees registered for this Meetpoint program may enter"
                            data-testid={`stage-eligibility-badge-${stage.id}`}
                          >
                            Eligibility
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {stage.capacity ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {stage.active_from && stage.active_to
                        ? formatRange(stage.active_from, stage.active_to)
                        : "Inherits event"}
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className="text-accent hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDrillStage(stage);
                        }}
                        data-testid={`stage-operators-link-${stage.id}`}
                      >
                        {opCount} →
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingStage(stage);
                          setAdding(false);
                          setFormError(null);
                        }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {adding ? (
        <StageForm
          event={event}
          onCancel={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            reload();
          }}
          error={formError}
          setError={setFormError}
        />
      ) : null}

      {editingStage ? (
        <Dialog
          open
          onClose={() => {
            setEditingStage(null);
            setFormError(null);
          }}
          labelledBy="stage-edit-title"
        >
          <DialogHeader
            id="stage-edit-title"
            title={`Edit ${editingStage.name}`}
            description="Stage details. Changes save immediately."
            onClose={() => {
              setEditingStage(null);
              setFormError(null);
            }}
          />
          <DialogBody>
            <StageForm
              event={event}
              stage={editingStage}
              onCancel={() => setEditingStage(null)}
              onSaved={() => {
                setEditingStage(null);
                reload();
              }}
              onDeleted={() => {
                setEditingStage(null);
                reload();
              }}
              error={formError}
              setError={setFormError}
              embedded
            />
          </DialogBody>
        </Dialog>
      ) : null}


      {drillStage ? (
        <Dialog
          open
          onClose={() => setDrillStage(null)}
          labelledBy="stage-operators-title"
        >
          <DialogHeader
            id="stage-operators-title"
            title={`Operators for ${drillStage.name}`}
            description="Assign operators who will scan badges at this stage."
            onClose={() => setDrillStage(null)}
          />
          <DialogBody>
            <StageOperatorsSubscreen
              event={event}
              stage={drillStage}
              onChanged={() => {
                // Force the operator-count column to refresh.
                counts.reload();
              }}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDrillStage(null)}>
              Close
            </Button>
          </DialogFooter>
        </Dialog>
      ) : null}
    </div>
  );
}

// ---- Stage create/edit form ------------------------------------------------

function StageForm({
  event,
  stage,
  onCancel,
  onSaved,
  onDeleted,
  error,
  setError,
  embedded
}: {
  event: Event;
  stage?: Stage;
  onCancel: () => void;
  onSaved: () => void;
  onDeleted?: () => void;
  error: string | null;
  setError: (message: string | null) => void;
  /** When true, render plain (no outer Card / heading) — used inside a Dialog. */
  embedded?: boolean;
}): JSX.Element {
  const [name, setName] = useState(stage?.name ?? "");
  const [capacity, setCapacity] = useState(
    stage?.capacity != null ? String(stage.capacity) : ""
  );
  const [activeFrom, setActiveFrom] = useState(
    stage?.active_from ? toDateTimeLocal(stage.active_from) : ""
  );
  const [activeTo, setActiveTo] = useState(
    stage?.active_to ? toDateTimeLocal(stage.active_to) : ""
  );
  const [displayOrder, setDisplayOrder] = useState(
    String(stage?.display_order ?? 0)
  );
  // CR-08. Per-stage Meetpoint program eligibility. Only effective when the
  // stage is linked to an MP program (meetpoint_program_id set by sync); the
  // checkbox is disabled otherwise.
  const [enforceProgram, setEnforceProgram] = useState(
    stage?.enforce_program_eligibility ?? false
  );
  const programLinked = stage?.meetpoint_program_id != null;
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (activeFrom && activeTo && new Date(activeTo) <= new Date(activeFrom)) {
      setError("The end time must be after the start time.");
      return;
    }
    const payload: CreateStageRequest = {
      name: name.trim(),
      capacity: capacity ? Number(capacity) : null,
      active_from: activeFrom ? fromDateTimeLocal(activeFrom) : null,
      active_to: activeTo ? fromDateTimeLocal(activeTo) : null,
      display_order: Number(displayOrder) || 0,
      enforce_program_eligibility: enforceProgram
    };
    setSaving(true);
    try {
      if (stage) {
        await api.updateStage(stage.id, payload);
      } else {
        await api.createStage(event.id, payload);
      }
      onSaved();
    } catch (err) {
      setError(isApiError(err) ? err.message : "Could not save the stage.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!stage || !onDeleted) {
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await api.deleteStage(stage.id);
      onDeleted();
    } catch (err) {
      if (isApiError(err) && err.code === "STAGE_HAS_SCANS") {
        setError(
          "Cannot delete: this stage has scan history. Close the event instead."
        );
      } else {
        setError(isApiError(err) ? err.message : "Could not delete the stage.");
      }
    } finally {
      setSaving(false);
    }
  };

  // CR-06 §4.1 — flip a meetpoint-imported stage to manual so the next
  // sync skips it. One-way per spec; an admin who undid this would have to
  // touch SQL (the MP link is preserved on the row).
  const handleConvertToManual = async (): Promise<void> => {
    if (!stage) {
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await api.convertStageSource(stage.id, "manual");
      onSaved();
    } catch (err) {
      setError(
        isApiError(err) ? err.message : "Could not convert the stage."
      );
    } finally {
      setSaving(false);
    }
  };

  const body = (
    <form className="space-y-3" onSubmit={handleSubmit}>
        <div className="space-y-1.5">
          <Label htmlFor="stage-name">Name</Label>
          <Input
            id="stage-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={1}
          />
          {stage?.source === "meetpoint" ? (
            <p className="text-xs text-muted-foreground">
              Imported from Meetpoint. Next sync will overwrite this name if
              Meetpoint renames the room. Convert to manual to lock the name.
            </p>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="stage-capacity">Capacity (optional)</Label>
            <Input
              id="stage-capacity"
              type="number"
              min={1}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              placeholder="Unlimited"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="stage-order">Display order</Label>
            <Input
              id="stage-order"
              type="number"
              min={0}
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="stage-from">Active from (optional)</Label>
            <Input
              id="stage-from"
              type="datetime-local"
              value={activeFrom}
              onChange={(e) => setActiveFrom(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="stage-to">Active to (optional)</Label>
            <Input
              id="stage-to"
              type="datetime-local"
              value={activeTo}
              onChange={(e) => setActiveTo(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input"
              checked={enforceProgram}
              disabled={!programLinked}
              onChange={(e) => setEnforceProgram(e.target.checked)}
              data-testid="stage-enforce-program"
            />
            Enforce Meetpoint program eligibility
          </label>
          <p className="text-xs text-muted-foreground">
            {programLinked
              ? "Only attendees registered for this stage's Meetpoint program may enter. Others are rejected at the door."
              : "Available only on stages linked to a Meetpoint program (imported by sync)."}
          </p>
        </div>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <div className="flex flex-wrap justify-end gap-2">
          {stage?.source === "meetpoint" ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                void handleConvertToManual();
              }}
              disabled={saving}
              data-testid={`stage-convert-manual-${stage.id}`}
            >
              Convert to manual
            </Button>
          ) : null}
          {stage && onDeleted ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                void handleDelete();
              }}
              disabled={saving}
            >
              Delete
            </Button>
          ) : null}
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={saving || !name.trim()}>
            {saving ? "Saving…" : stage ? "Save changes" : "Create stage"}
          </Button>
        </div>
      </form>
  );

  if (embedded) {
    return body;
  }
  return (
    <Card className="p-4">
      <h3 className="mb-3 text-sm font-semibold">
        {stage ? `Edit ${stage.name}` : "Add a stage"}
      </h3>
      {body}
    </Card>
  );
}

// ---- Stage-operator assignment subscreen -----------------------------------

interface SubscreenData {
  assigned: Operator[];
  eventOperators: Operator[];
  customerOperators: Operator[];
}

function StageOperatorsSubscreen({
  event,
  stage,
  onChanged
}: {
  event: Event;
  stage: Stage;
  onChanged: () => void;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{
    operatorId: string;
    operatorName: string;
    events: OverlapConflict[];
  } | null>(null);
  const [selected, setSelected] = useState("");

  const { data, loading, error: loadError, reload } = useAsync<SubscreenData>(
    async () => {
      const [assigned, eventOperators, customerOperators] = await Promise.all([
        api.listStageOperators(stage.id),
        api.listEventOperators(event.id),
        api.listOperators(event.customer_id)
      ]);
      return { assigned, eventOperators, customerOperators };
    },
    [stage.id, event.id, event.customer_id]
  );

  if (loading) {
    return <LoadingState />;
  }
  if (loadError || !data) {
    return <ErrorState message={loadError ?? "Failed to load."} onRetry={reload} />;
  }

  const assignedIds = new Set(data.assigned.map((o) => o.id));
  const eventOperatorIds = new Set(data.eventOperators.map((o) => o.id));

  // Eligible: assigned to the event AND not already on this stage.
  // Disabled-with-hint: belongs to the customer but is not yet on the event
  // (CR-01 §3.4 — the parent-event assignment is a prerequisite).
  const eligible = data.eventOperators.filter((o) => !assignedIds.has(o.id));
  const needsEventAssignment = data.customerOperators.filter(
    (o) => !eventOperatorIds.has(o.id)
  );

  const handleAssign = async (): Promise<void> => {
    if (!selected) {
      return;
    }
    setError(null);
    const operator = eligible.find((o) => o.id === selected);
    try {
      await api.assignStageOperator(stage.id, selected);
      setSelected("");
      reload();
      onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.code === "OVERLAP" && err.conflicts) {
        setConflict({
          operatorId: selected,
          operatorName: operator?.name ?? "Operator",
          events: err.conflicts
        });
      } else if (isApiError(err) && err.code === "OPERATOR_NOT_ON_EVENT") {
        setError("This operator must be assigned to the event first.");
      } else if (isApiError(err) && err.code === "CROSS_CUSTOMER") {
        setError("That operator belongs to a different customer.");
      } else {
        setError(
          isApiError(err) ? err.message : "Could not assign the operator."
        );
      }
    }
  };

  const handleUnassign = async (operatorId: string): Promise<void> => {
    setError(null);
    try {
      await api.unassignStageOperator(stage.id, operatorId);
      reload();
      onChanged();
    } catch (err) {
      setError(
        isApiError(err) ? err.message : "Could not unassign the operator."
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="assign-stage-operator">Assign an operator</Label>
          <Select
            id="assign-stage-operator"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">Select an operator…</option>
            {eligible.map((op) => (
              <option key={op.id} value={op.id}>
                {op.name} ({op.email})
              </option>
            ))}
            {needsEventAssignment.length > 0 ? (
              <optgroup label="Not on this event yet — assign on Operators tab">
                {needsEventAssignment.map((op) => (
                  <option key={op.id} value={op.id} disabled>
                    {op.name} ({op.email})
                  </option>
                ))}
              </optgroup>
            ) : null}
          </Select>
        </div>
        <Button onClick={handleAssign} disabled={!selected}>
          Assign
        </Button>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {data.assigned.length === 0 ? (
        <EmptyState
          title="No operators on this stage"
          description="Assign operators so they can scan badges at this door."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Operator</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.assigned.map((op) => (
              <TableRow key={op.id}>
                <TableCell className="font-medium">{op.name}</TableCell>
                <TableCell className="text-muted-foreground">{op.email}</TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      void handleUnassign(op.id);
                    }}
                  >
                    Unassign
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {conflict ? (
        <Dialog
          open
          onClose={() => setConflict(null)}
          labelledBy="stage-overlap-title"
        >
          <DialogHeader
            id="stage-overlap-title"
            title="Assignment conflict"
            description={`${conflict.operatorName} is already on event(s) whose schedule overlaps this one.`}
            onClose={() => setConflict(null)}
          />
          <DialogBody>
            <ul className="space-y-2">
              {conflict.events.map((c) => (
                <li
                  key={c.id}
                  className="rounded-md border border-border p-3 text-sm"
                >
                  <p className="font-medium">{c.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatRange(c.active_from, c.active_to)}
                  </p>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">
              An operator cannot cover two events at the same time. Unassign
              them from the conflicting event on its Operators tab, then try
              the stage assignment again.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConflict(null)}>
              Close
            </Button>
          </DialogFooter>
        </Dialog>
      ) : null}
    </div>
  );
}
